import { apiRequest } from './core/client';

// ============================================================================
// Packages (incoming shipments, EasyPost-backed)
// ============================================================================

export interface PackageScan {
  status: string;
  message?: string;
  datetime?: string;
  tracking_location?: {
    city?: string | null;
    state?: string | null;
    country?: string | null;
    zip?: string | null;
  };
}

export interface Package {
  id: string;
  trackingCode: string;
  carrier: string | null;
  easypostTrackerId: string | null;
  status: string;
  statusDetail: string | null;
  estDeliveryDate: string | null;
  deliveredAt: string | null;
  signedBy: string | null;
  publicUrl: string | null;
  vendor: string | null;
  orderRef: string | null;
  packageCount: number;
  itemsSummary: string | null;
  notes: string | null;
  messageRefs: Array<{ message_id: string }>;
  trackingDetails: PackageScan[];
  lastCarrierUpdate: string | null;
  archivedAt: string | null;
  problemResolution: string | null;
  problemResolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Carrier statuses that need a human until someone records how they were settled. */
export const PROBLEM_STATUSES: ReadonlySet<string> = new Set(['failure', 'return_to_sender', 'error']);

type ProblemFields = { status: string; problemResolution?: string | null };

/** In a problem status and nobody has settled it: the Follow-ups / chip / calendar flag. */
export function isOpenProblem(p: ProblemFields): boolean {
  return PROBLEM_STATUSES.has(p.status) && !p.problemResolution;
}

/** A delivery problem someone has settled. Finished news: it neither arrives nor alarms. */
export function isResolvedProblem(p: ProblemFields): boolean {
  return PROBLEM_STATUSES.has(p.status) && !!p.problemResolution;
}

export async function getPackages(options: { deliveredDays?: number } = {}): Promise<Package[]> {
  const response = await apiRequest<{ packages: Package[] }>({
    path: '/packages',
    method: 'GET',
    query: options.deliveredDays ? { delivered_days: options.deliveredDays } : undefined,
  });
  return Array.isArray(response.packages) ? response.packages : [];
}

export interface RegisterPackageInput {
  tracking_code: string;
  carrier?: string;
  vendor?: string;
  order_ref?: string;
  package_count?: number;
}

export interface RegisterPackageResult {
  success: boolean;
  package: Package;
  easypost_error: string | null;
}

export async function registerPackage(input: RegisterPackageInput): Promise<RegisterPackageResult> {
  return apiRequest<RegisterPackageResult>({
    path: '/packages',
    method: 'POST',
    body: { ...input, source: 'manual' },
  });
}

export async function refreshPackages(): Promise<{ checked: number; updated: number }> {
  return apiRequest<{ checked: number; updated: number }>({
    path: '/packages/refresh',
    method: 'POST',
  });
}

export async function archivePackage(id: string): Promise<Package> {
  const response = await apiRequest<{ package: Package }>({
    path: `/packages/${encodeURIComponent(id)}`,
    method: 'PATCH',
    body: { archived: true },
  });
  return response.package;
}

/**
 * Close a delivery problem by recording how it was settled ("lost; vendor
 * reshipped free"). The parcel stays on its journey and calendar but leaves
 * every problem queue. Pass null to reopen it. The server rejects this for a
 * parcel that is not in a problem status.
 */
export async function setPackageProblemResolution(id: string, resolution: string | null): Promise<Package> {
  const response = await apiRequest<{ package: Package }>({
    path: `/packages/${encodeURIComponent(id)}`,
    method: 'PATCH',
    body: { problem_resolution: resolution },
  });
  return response.package;
}

/**
 * Record that a parcel legitimately belongs to no order journey — a sample, a
 * warranty replacement, swatches from a rep. It leaves the unlinked-parcels
 * queue but stays tracked, with its calendar event intact; archiving would take
 * the event with it, which is wrong for something that really was delivered.
 * Pass null to put it back in the queue.
 */
export async function setPackageNoJourneyReason(id: string, reason: string | null): Promise<Package> {
  const response = await apiRequest<{ package: Package }>({
    path: `/packages/${encodeURIComponent(id)}`,
    method: 'PATCH',
    body: { no_journey_reason: reason },
  });
  return response.package;
}
